import store from '../store';

import { makeGetMasterToken, makeGetTeamToken, makeGetTeamUrl } from '../selectors/auth';
import { makeGetCurrentTeam, makeGetCurrentTeamUrl, makeGetCurrentTeamId } from '../selectors/teams';
import { makeGetCurrentUser, makeGetCurrentUserId, makeGetUser, makeGetNonGuestTeamUsers, makeGetGuestTeamUsers, makeGetResolvedActiveNonGuestTeamUsers } from '../selectors/users';
import { makeGetActiveProjects, makeGetProject } from '../selectors/projects';
import { makeGetSubteam } from '../selectors/subteams';
import { makeGetDirectChatByMembers } from '../selectors/discussions';
import { makeGetSortedActiveProjectTaskGroups, makeGetTaskGroup } from '../selectors/task-groups';
import { makeGetTask, makeGetTaskGroupOpenTasks, makeGetMilestoneTasks, makeGetMovedTaskOrder } from '../selectors/tasks';
import { makeGetFile } from '../selectors/files';
import { makeGetSubscriptionIsExpired, makeGetIsOnTrial, makeGetCurrentPlan } from '../selectors/billing';
import { makeGetFetchedState, makeGetLoadingState } from '../selectors/global';
import { makeGetVisibleChats } from '../selectors/user-state';

const getMasterTokenFn = makeGetMasterToken();
const getTeamTokenFn = makeGetTeamToken();
const getTeamUrlFn = makeGetTeamUrl();

const getCurrentTeamFn = makeGetCurrentTeam();
const getCurrentTeamUrlFn = makeGetCurrentTeamUrl();
const getCurrentTeamIdFn = makeGetCurrentTeamId();

const getCurrentUserFn = makeGetCurrentUser();
const getCurrentUserIdFn = makeGetCurrentUserId();
const getUserFn = makeGetUser();
const getNonGuestTeamUsersFn = makeGetNonGuestTeamUsers();
const getGuestTeamUsersFn = makeGetGuestTeamUsers();
const getResolvedActiveNonGuestTeamUsersFn = makeGetResolvedActiveNonGuestTeamUsers();

const getActiveProjectsFn = makeGetActiveProjects();
const getProjectFn = makeGetProject();

const getSubteamFn = makeGetSubteam();

const getDirectChatByMembersFn = makeGetDirectChatByMembers();

const getTaskGroupFn = makeGetTaskGroup();

const getTaskFn = makeGetTask();

const getFileFn = makeGetFile();

const getSubscriptionIsExpiredFn = makeGetSubscriptionIsExpired();
const getIsOnTrialFn = makeGetIsOnTrial();
const getCurrentPlanFn = makeGetCurrentPlan();

const getMilestonesFetchedFn = makeGetFetchedState();
const getMilestonesLoadingFn = makeGetLoadingState();

const getSortedActiveProjectTaskGroupsFn = makeGetSortedActiveProjectTaskGroups();
const getTaskGroupsFetchedFn = makeGetFetchedState();
const getTaskGroupsLoadingFn = makeGetLoadingState();

const getTaskGroupOpenTasksFn = makeGetTaskGroupOpenTasks();
const getMilestoneTasksFn = makeGetMilestoneTasks();
const getTasksFetchedFn = makeGetFetchedState();
const getTasksLoadingFn = makeGetLoadingState();

const getVisibleChatsFn = makeGetVisibleChats();
const getMovedTaskOrderFn = makeGetMovedTaskOrder();

// Get from store
export const getMasterToken = () => getMasterTokenFn(store.getState().auth);
export const getTeamToken = teamUrl => getTeamTokenFn(store.getState().auth, teamUrl);
export const getTeamUrl = () => getTeamUrlFn(store.getState().auth);

export const getCurrentTeam = () => getCurrentTeamFn(store.getState().teams);
export const getCurrentTeamUrl = () => getCurrentTeamUrlFn(store.getState().teams);
export const getCurrentTeamId = () => getCurrentTeamIdFn(store.getState().teams);

export const getCurrentUser = () => getCurrentUserFn(store.getState().users);
export const getCurrentUserId = () => getCurrentUserIdFn(store.getState().users);
export const getUser = userId => getUserFn(store.getState().users, userId);
export const getNonGuestTeamUsers = () => getNonGuestTeamUsersFn(getCurrentTeam(), store.getState().users);
export const getGuestTeamUsers = () => getGuestTeamUsersFn(getCurrentTeam(), store.getState().users);
export const getResolvedActiveNonGuestTeamUsers = () => getResolvedActiveNonGuestTeamUsersFn(getCurrentTeam(), store.getState().users);

export const getActiveProjects = () => getActiveProjectsFn(store.getState().projects.items);
export const getProject = projectId => getProjectFn(store.getState().projects.items, projectId);

export const getSubteam = subteamId => getSubteamFn(store.getState().subteams.items, subteamId);

export const getDirectChatByMembers = userIds => getDirectChatByMembersFn(store.getState().discussions.items, userIds);

export const getTaskGroup = taskGroupId => getTaskGroupFn(store.getState().taskGroups.items, taskGroupId);

export const getTask = taskId => getTaskFn(store.getState().tasks.items, taskId);

export const getFile = fileId => getFileFn(store.getState().files.items, fileId);

export const getMilestone = milestoneId => getFileFn(store.getState().milestones.items, milestoneId);

export const getSubscriptionIsExpired = () => getSubscriptionIsExpiredFn(getCurrentTeam());
export const getIsOnTrial = () => getIsOnTrialFn(getCurrentTeam());
export const getCurrentPlan = () => getCurrentPlanFn(getCurrentTeam());

export const getTaskGroupOpenTasks = taskGroupId => getTaskGroupOpenTasksFn(store.getState().tasks.items, taskGroupId);
export const getMilestoneTasks = milestoneId => getMilestoneTasksFn(store.getState().tasks.items, milestoneId);

export const getMilestonesFetched = projectId => getMilestonesFetchedFn(store.getState().milestones.fetchInfo, 'project', projectId);
export const getMilestonesLoading = projectId => getMilestonesLoadingFn(store.getState().milestones.fetchInfo, 'project', projectId);

export const getSortedActiveProjectTaskGroups = projectId => getSortedActiveProjectTaskGroupsFn(store.getState().taskGroups.items, projectId);
export const getTaskGroupsFetched = projectId => getTaskGroupsFetchedFn(store.getState().taskGroups.fetchInfo, 'project', projectId);
export const getTaskGroupsLoading = projectId => getTaskGroupsLoadingFn(store.getState().taskGroups.fetchInfo, 'project', projectId);

export const getTasksFetched = (association, associationId) => getTasksFetchedFn(store.getState().tasks.fetchInfo, association, associationId);
export const getTasksLoading = (association, associationId) => getTasksLoadingFn(store.getState().tasks.fetchInfo, association, associationId);
export const getVisibleChats = () => getVisibleChatsFn(store.getState().userState.state);

export const getMovedTaskOrder = (taskId, targetTaskGroupId, targetTaskId, isBefore) => getMovedTaskOrderFn(store.getState().tasks.items, taskId, targetTaskGroupId, targetTaskId, isBefore);

// Subscribe to store
const select = (state, path) => path.reduce((newState, key) => newState && newState[key], state);

export const storeSubscribe = (path, callback) => {
    let currentState = null;
    let prevState = null;

    store.subscribe(() => {
        prevState = currentState;
        currentState = select(store.getState(), path);

        if (currentState !== prevState) {
            callback(currentState, prevState);
        }
    });
}
